import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';

import { Box, BoxProps, SelectField, COUNTRIES } from '@electricjs/arc';

import { ActionConfirmationModal } from '@common';

import i18n from '@/i18n';
import { CURRENT_SHOPPING_COUNTRY_CODES } from '@/constants/storefront';
import { ShoppingCountryOption } from '@/types/storefront';
import { getCountryName } from '@/helpers/getCountryName';
import { useStorefront } from '@/components/Storefront/StorefrontProvider';
import { useGlobalUI } from '@/components/GlobalUIProvider';

export const getShoppingCountryOptions = (): ShoppingCountryOption[] => {
  const countryOptions = CURRENT_SHOPPING_COUNTRY_CODES.map(code => {
    return {
      label: `${COUNTRIES[code]?.flag} ${i18n.t('storefront:shoppingCountrySelector.countryLabel', { name: getCountryName(code) })}`,
      value: code,
    };
  });

  return [
    ...countryOptions,
    {
      label: `\u{1F30F}\u{FE0E} ${i18n.t('storefront:shoppingCountrySelector.otherCountriesLabel')}`,
      value: 'other',
    },
  ];
};

type ShoppingCountrySelectorProps = Pick<BoxProps, 'width' | 'mt'>;

export const ShoppingCountrySelector = ({
  width,
  mt,
}: ShoppingCountrySelectorProps) => {
  const { t } = useTranslation('storefront');
  const { storefrontInternational } = useFlags();
  const { openModal, closeModal } = useGlobalUI();

  const { storefrontCheckoutForm, clearAllCarts, itemsInCart } =
    useStorefront();

  if (!storefrontCheckoutForm) {
    return null;
  }

  const applyCountryChange = () => {
    // clear carts (to avoid multi-country carts)
    clearAllCarts();

    // clear shipping address (as the address won't exist in the new country)
    storefrontCheckoutForm?.setValue('shippingAddress', {
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      state: '',
      country: '',
      zip: '',
    });

    // clear selected shipping methods (as each country has its own options)
    storefrontCheckoutForm?.setValue('catalogShippingMethod', undefined);
    storefrontCheckoutForm?.setValue('warehouseShippingMethod', undefined);
  };

  return (
    <Box width={width} minWidth={width ? undefined : '27rem'} mt={mt}>
      <SelectField
        id="shopping-country-selector"
        name="shoppingCountry"
        key={storefrontCheckoutForm?.getValues('shoppingCountry')?.value}
        control={storefrontCheckoutForm?.control}
        onChange={() => {
          if (!itemsInCart) {
            applyCountryChange();
            return;
          }

          const previousShoppingCountry =
            storefrontCheckoutForm?.getValues('shoppingCountry');

          openModal(
            <ActionConfirmationModal
              title={t('shoppingCountrySelector.confirmChangeModal.title')}
              message={t('shoppingCountrySelector.confirmChangeModal.message')}
              cancelButtonLabel={t(
                'shoppingCountrySelector.confirmChangeModal.cancelButton'
              )}
              confirmButtonLabel={t(
                'shoppingCountrySelector.confirmChangeModal.confirmButton'
              )}
              confirmButtonIntent="primary"
              onCancel={() => {
                storefrontCheckoutForm?.setValue(
                  'shoppingCountry',
                  previousShoppingCountry
                );
                closeModal();
              }}
              onConfirm={() => {
                applyCountryChange();
                closeModal();
                return Promise.resolve();
              }}
            />
          );
        }}
        options={getShoppingCountryOptions()}
        disabled={!storefrontInternational}
      />
    </Box>
  );
};
